import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { CurrencyCode, PricingPackage } from '@ndvr/billing-api';

import {
    FormMoneyInput,
    FormNumberInput,
    FormSelect,
    HorizontalLayout,
    RadioButton,
    Text,
    VerticalLayout,
} from 'widgets';

import { PricingInfo } from './PricingInfo';

const optionTitles = ['Claro Pricing Package', 'Custom Profile'];

interface Props {
    pricingPackages: PricingPackage[];
    defaultRadioIndex?: number;
}

export const PricingSelect = ({ pricingPackages, defaultRadioIndex = 0 }: Props) => {
    const { watch, setValue } = useFormContext();
    const pricingPackageGUID = watch('pricingPackageGUID');
    const fixedFee = watch('fixedFee');
    const annualBps = watch('annualBps');

    const [selectedIndex, setSelectedIndex] = useState(defaultRadioIndex);

    const onRadioChange = (index: number) => {
        if (index !== selectedIndex) {
            setSelectedIndex(index);
            if (index === 0) {
                setValue('pricingPackageGUID', pricingPackages[0]?.pricingPackageGUID);
                setValue('fixedFee', undefined);
                setValue('annualBps', undefined);
            } else {
                setValue('pricingPackageGUID', undefined);
                setValue('fixedFee', {
                    money: { currency: CurrencyCode.USD, amount: '' },
                });
                setValue('annualBps', '');
            }
        }
    };

    return (
        <>
            {optionTitles.map((optionTitle, index) => (
                <OptionBox key={index} $selected={selectedIndex === index}>
                    <HorizontalLayout $apart $stretch>
                        <RadioButton
                            value={index}
                            onClick={() => onRadioChange(index)}
                            checked={selectedIndex === index}
                            label={<Text largeBody>{optionTitle}</Text>}
                        />
                        {selectedIndex === index && (
                            <PricingInfo
                                pricingPackages={pricingPackages}
                                pricingPackageGUID={pricingPackageGUID}
                                fixedFee={fixedFee}
                                annualBps={annualBps}
                                alignment="end"
                            />
                        )}
                    </HorizontalLayout>

                    <VerticalLayout $margin="0 0 0 24px">
                        {selectedIndex === index && index === 0 && (
                            <FormSelect name="pricingPackageGUID" label="Select Package" vertical>
                                {pricingPackages.map((pricingPackage) => (
                                    <option
                                        key={pricingPackage.pricingPackageGUID}
                                        value={pricingPackage.pricingPackageGUID}
                                    >
                                        {pricingPackage.pricingPackageName}
                                    </option>
                                ))}
                            </FormSelect>
                        )}
                        {selectedIndex === index && index === 1 && (
                            <VerticalLayout $gap={16}>
                                <FormMoneyInput
                                    name="fixedFee.money.amount"
                                    label="Membership Fee"
                                    placeholder="enter monthly dollar amount"
                                    decimalScale={0}
                                    vertical
                                />
                                <FormNumberInput
                                    name="annualBps"
                                    label="Annual AUM Charge"
                                    placeholder="enter annual bps"
                                    decimalScale={2}
                                    allowNegative={false}
                                    vertical
                                />
                            </VerticalLayout>
                        )}
                    </VerticalLayout>
                </OptionBox>
            ))}
        </>
    );
};

const OptionBox = styled.div<{ $selected?: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 16px 18px 16px 12px;
    border: 1px solid ${({ theme, $selected }) => ($selected ? theme.border.active : theme.border.dark)};
    border-radius: 3px;

    &:not(:last-child) {
        margin-bottom: 16px;
    }
`;
